import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  GitBranch,
  FileText,
  Info,
  History,
  ExternalLink,
  Download,
  TestTube,
  RotateCcw,
  Save,
  AlertCircle,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import PermissionMatrixTab from './components/PermissionMatrixTab';
import ApprovalWorkflowsTab from './components/ApprovalWorkflowsTab';
import ContentGuidelinesTab from './components/ContentGuidelinesTab';
import { todayLocalDateParam } from '@/utils/apiDate';

interface Tab {
  id: string;
  label: string;
  icon: string;
}

interface AuditLog {
  user: string;
  action: string;
  time: string;
  icon: string;
}

const tabs: Tab[] = [
  { id: 'permissions', label: 'Permission Matrix', icon: 'Shield' },
  { id: 'workflows', label: 'Approval Workflows', icon: 'GitBranch' },
  { id: 'guidelines', label: 'Content Guidelines', icon: 'FileText' },
];

const auditLogs: AuditLog[] = [
  { user: 'Admin User', action: 'Updated permission matrix for Manager role', time: '2 hours ago', icon: 'Shield' },
  {
    user: 'HR Manager',
    action: 'Modified approval workflow for profile photo changes',
    time: '5 hours ago',
    icon: 'GitBranch',
  },
  {
    user: 'System Admin',
    action: 'Added new content guideline for skills section',
    time: '1 day ago',
    icon: 'FileText',
  },
];

const getTabIcon = (iconName: string) => {
  switch (iconName) {
    case 'Shield':
      return <Shield className="w-[18px] h-[18px] mr-2" />;
    case 'GitBranch':
      return <GitBranch className="w-[18px] h-[18px] mr-2" />;
    case 'FileText':
      return <FileText className="w-[18px] h-[18px] mr-2" />;
    default:
      return <Shield className="w-[18px] h-[18px] mr-2" />;
  }
};

const getAuditIcon = (iconName: string) => {
  switch (iconName) {
    case 'Shield':
      return <Shield className="w-4 h-4 text-blue-600" />;
    case 'GitBranch':
      return <GitBranch className="w-4 h-4 text-blue-600" />;
    case 'FileText':
      return <FileText className="w-4 h-4 text-blue-600" />;
    default:
      return <Shield className="w-4 h-4 text-blue-600" />;
  }
};

export default function ProfileAuthorization() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('permissions');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);

    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      setHasUnsavedChanges(false);
      alert('Authorization settings saved successfully!');
    }, 1500);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset to default settings? This cannot be undone.')) {
      setHasUnsavedChanges(false);
      window.location.reload();
    }
  };

  const handleTestPolicy = () => {
    setIsTestMode(!isTestMode);
  };

  const handleExportSettings = () => {
    const settings = {
      permissions: 'Current permission settings',
      workflows: 'Current workflow settings',
      guidelines: 'Current guideline settings',
      exportedAt: new Date().toISOString(),
    };

    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `profile-authorization-settings-${todayLocalDateParam()}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'permissions':
        return <PermissionMatrixTab onSettingsChange={() => setHasUnsavedChanges(true)} isTestMode={isTestMode} />;
      case 'workflows':
        return <ApprovalWorkflowsTab onSettingsChange={() => setHasUnsavedChanges(true)} />;
      case 'guidelines':
        return <ContentGuidelinesTab onSettingsChange={() => setHasUnsavedChanges(true)} />;
      default:
        return <PermissionMatrixTab onSettingsChange={() => setHasUnsavedChanges(true)} isTestMode={isTestMode} />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<Shield className="w-6 h-6" />}
        title="Profile Authorization Settings"
        description="Profile Authorization Settings is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-sky-500"
        gradientTo="to-blue-500"
      />

      {/* Header */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('Profile Authorization Settings')}</h1>
              <p className="text-gray-600 mt-1">
                {t('Comprehensive control over public profile permissions, approval workflows, and content moderation')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {hasUnsavedChanges && (
              <div className="flex items-center text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                {t('Unsaved changes')}
              </div>
            )}

            {isTestMode && (
              <div className="flex items-center text-blue-600 text-sm">
                <TestTube className="w-4 h-4 mr-1" />
                {t('Test Mode Active')}
              </div>
            )}

            <button
              onClick={handleExportSettings}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              {t('Export')}
            </button>

            <button
              onClick={handleTestPolicy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <TestTube className="w-4 h-4" />
              {isTestMode ? t('Exit Test') : t('Test Policy')}
            </button>

            <button
              onClick={handleReset}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <RotateCcw className="w-4 h-4" />
              {t('Reset to Default')}
            </button>

            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isSaving}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg ${
                !hasUnsavedChanges || isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
              }`}
            >
              <Save className="w-4 h-4" />
              {isSaving ? t('Saving...') : t('Save Changes')}
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">{t('Administrative Control Center')}</h3>
            <p className="text-sm text-blue-800">
              {t(
                'Configure granular permissions for profile sections, set up approval workflows for profile changes, and establish content guidelines with automated validation. Changes affect all organizational profile policies.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-card rounded-xl shadow-sm border border-border mb-6">
        <div className="border-b border-border">
          <nav className="flex space-x-1 p-2" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {getTabIcon(tab.icon)}
                {t(tab.label)}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">{renderTabContent()}</div>
      </div>

      {/* Audit Trail Section */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <History className="w-5 h-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">{t('Recent Changes')}</h3>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
            <ExternalLink className="w-4 h-4" />
            {t('View Full Audit Log')}
          </button>
        </div>

        <div className="space-y-3">
          {auditLogs.map((log, index) => (
            <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                {getAuditIcon(log.icon)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{t(log.action)}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {log.user} &bull; {log.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
